import { PricingItem } from './types';

/**
 * Returns a shallow copy of an object with the specified keys removed.
 * If the input object is null or undefined, it is returned as-is.
 *
 * @template T
 * @param obj - The source object.
 * @param keys - List of property names to omit.
 * @returns A new object without the specified keys, or the original value if it is null/undefined.
 */
export function omitKeys<T extends Record<string, any>>(
	obj: T | null | undefined,
	keys: string[],
): Partial<T> | null | undefined {
	if (!obj) return obj;
	const result = { ...obj };
	for (const key of keys) {
		delete result[key];
	}
	return result;
}

/**
 * Base64-encodes a materials object so it can be embedded as a flat string value.
 *
 * @param materials - Raw materials value/object.
 * @returns Base64 encoded JSON string.
 */
export function encodeMaterials(materials: unknown): string {
	return window.btoa(JSON.stringify(materials));
}

/**
 * Normalizes raw price-condition data into the snapshot's pricing item shape.
 *
 * @param data - Raw pricing condition array.
 * @returns Normalized pricing items array.
 */
export function convertPricingData(data: any): PricingItem[] {
	const items = Array.isArray(data) ? data : [];
	return items.map((item) => ({
		materials: encodeMaterials(item.materials),
		price_type: item.price_type,
		unit_price: item.price,
	}));
}

/**
 * Shared helper for single-array catalogs: keeps only the items matching `predicate`.
 * Returns the input unchanged when it is not an array, mirroring the original per-catalog
 * guards. Callers precompute their DOM selection state once and close over it in `predicate`,
 * so filtering stays O(items) instead of running a fresh DOM query per item.
 *
 * @param catalog - Catalog array or raw data.
 * @param predicate - Filter predicate function.
 */
export function filterFlatCatalog<T = any>(catalog: any, predicate: (item: T) => boolean): any {
	if (!Array.isArray(catalog)) {
		return catalog;
	}
	return catalog.filter(predicate);
}
